/**
 * The rendered voice pack (docs/TIBBS-VOICE.md 5.5): public/voice/tibbs/,
 * listed in manifest.json. The service worker precaches the core tier at
 * install and keeps every clip it fetches; the extended tier is fetched in the
 * background the first time anyone asks Tibbs for something. Offline, the
 * selection only picks lines whose clips are on the device.
 */
import { segsOf } from './plan';
import type { VoiceManifest } from './plan';

export const MANIFEST_URL = '/voice/tibbs/manifest.json';
export const MUSIC_URL = '/audio/ceremony-largo.mp3';
/** Must match src/sw/sw.js. */
export const VOICE_CACHE_PREFIX = 'ancientnine-voice-';

let manifestP: Promise<VoiceManifest | null> | null = null;

export function loadManifest(): Promise<VoiceManifest | null> {
  if (!manifestP) {
    manifestP = fetch(MANIFEST_URL)
      .then((r) => (r.ok ? (r.json() as Promise<VoiceManifest>) : null))
      .catch(() => null);
    manifestP.then((m) => {
      if (!m) manifestP = null; // try again next time (back online)
    });
  }
  return manifestP;
}

/** Bot (pack) name clips: seat name → URL, for the forms the manifest has. */
export function packNameUrl(m: VoiceManifest, name: string, form: 'plain' | 'proclaim'): string | null {
  const entry = m.names[name];
  const seg = entry?.[form];
  return seg ? (m.files[seg]?.file ?? null) : null;
}

/** Which clip files are on this device right now. Null means "assume all" (online, or no Cache Storage to ask). */
export async function cachedFiles(m: VoiceManifest): Promise<Set<string> | null> {
  if (typeof navigator !== 'undefined' && navigator.onLine !== false) return null;
  if (typeof caches === 'undefined') return null;
  try {
    const names = (await caches.keys()).filter((n) => n.startsWith(VOICE_CACHE_PREFIX));
    if (!names.length) return new Set();
    const out = new Set<string>();
    await Promise.all(
      Object.values(m.files).map(async (f) => {
        for (const n of names) {
          const c = await caches.open(n);
          if (await c.match(f.file)) {
            out.add(f.file);
            return;
          }
        }
      }),
    );
    return out;
  } catch {
    return null;
  }
}

/** A line can be played when every one of its clips is available. */
export function lineAvailable(m: VoiceManifest, files: Set<string> | null, lineId: string): boolean {
  if (!m.lines[lineId]) return false;
  if (!files) return true;
  return segsOf(m, lineId).every((s) => {
    const f = m.files[s];
    return !!f && files.has(f.file);
  });
}

let warming = false;

/** Fetch the extended tier in the background, a few at a time (the service worker keeps each one). */
export function warmExtended(m: VoiceManifest): void {
  if (warming || typeof fetch === 'undefined') return;
  warming = true;
  const files = Object.values(m.files)
    .filter((f) => f.tier === 'extended')
    .map((f) => f.file);
  files.push(MUSIC_URL);
  let i = 0;
  const worker = async () => {
    while (i < files.length) {
      const url = files[i++]!;
      try {
        await fetch(url);
      } catch {
        /* offline: the rest will come another time */
        return;
      }
    }
  };
  void Promise.all([worker(), worker(), worker()]).finally(() => {
    warming = false;
  });
}
